import { useLayoutEffect, useRef, useState } from 'react';

export function useDrawerTableSpacer(enabled: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const footerRef = useRef<HTMLTableSectionElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) {
      setSpacerHeight(0);
      return;
    }

    let animationFrame = 0;
    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    function measure() {
      cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const container = containerRef.current;
        const head = headRef.current;
        const body = bodyRef.current;
        const footer = footerRef.current;

        if (!container || !head || !body || !footer) {
          return;
        }

        const bodyRows = Array.from(
          body.querySelectorAll('[data-table-body-row="true"]')
        ) as HTMLElement[];
        const bodyRowsHeight = bodyRows.reduce(
          (total, row) => total + row.getBoundingClientRect().height,
          0
        );
        const availableBodyHeight =
          container.clientHeight -
          head.getBoundingClientRect().height -
          footer.getBoundingClientRect().height;
        const nextSpacerHeight = Math.max(
          0,
          Math.ceil(availableBodyHeight - bodyRowsHeight)
        );

        setSpacerHeight((currentHeight) =>
          Math.abs(currentHeight - nextSpacerHeight) <= 1
            ? currentHeight
            : nextSpacerHeight
        );
      });
    }

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (headRef.current) {
      resizeObserver.observe(headRef.current);
    }

    if (bodyRef.current) {
      resizeObserver.observe(bodyRef.current);
    }

    if (footerRef.current) {
      resizeObserver.observe(footerRef.current);
    }

    measure();
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled]);

  return {
    bodyRef,
    containerRef,
    footerRef,
    headRef,
    spacerHeight,
  };
}
